/**
 * Flow Layout
 *
 * Shared layered layout for service flow diagrams. Consumed by both the
 * existing PNG renderer (PDFKit / canvas) and the interactive inline
 * SVG renderer used by the HTML guide. Keeping the layout math here —
 * rather than duplicated per renderer — guarantees the PNG and SVG
 * outputs are geometrically identical, which matters because the PDF
 * and DOCX paths continue to embed PNGs.
 */

import type { GraphNode, GraphNodeType, ServiceGraph } from '../types/graph.js';

/** Layout constants — kept in sync with the legacy values in flow-renderer.ts. */
export const NODE_MIN_WIDTH = 100;
export const NODE_HEIGHT = 36;
export const NODE_PADDING = 20;
export const H_GAP = 30;
export const V_GAP = 50;
export const ARROW_SIZE = 6;
export const FONT_SIZE = 10;
export const CHAR_WIDTH = 6.2; // approximate width per character at 10px Arial

/** Color palette for node types. Used by every renderer for visual parity. */
export const NODE_COLORS: Record<GraphNodeType, { fill: string; stroke: string }> = {
  input: { fill: '#90EE90', stroke: '#5CBF5C' },
  output: { fill: '#87CEEB', stroke: '#5AAFCF' },
  filter: { fill: '#FFED6F', stroke: '#D4C44A' },
  processor: { fill: '#FFED6F', stroke: '#D4C44A' },
};

/** Padding around the laid-out content (left, top, right, bottom). */
export const LAYOUT_PADDING = 20;

/** A single positioned node in the computed layout. */
export interface LayoutNode {
  node: GraphNode;
  x: number;
  y: number;
  width: number;
}

/**
 * Computes the rendered width needed for a node based on its label.
 */
export function computeNodeWidth(label: string): number {
  return Math.max(NODE_MIN_WIDTH, label.length * CHAR_WIDTH + NODE_PADDING * 2);
}

/**
 * Computes a simple layered layout for the graph using topological
 * ordering with a barycenter crossing-reduction pass.
 */
export function computeLayout(graph: ServiceGraph): LayoutNode[] {
  return computeLayeredLayout(graph.nodes, graph.edges, {
    nodeHeight: NODE_HEIGHT,
    vGap: V_GAP,
    hGap: H_GAP,
    minWidth: NODE_MIN_WIDTH,
    padding: LAYOUT_PADDING,
    measureWidth: computeNodeWidth,
  });
}

/** Minimal node shape the generic layered layout operates on. */
export interface MinimalLayoutNode {
  id: string;
  label: string;
}

/** Tunable geometry for the generic layered layout. */
export interface LayeredLayoutOptions {
  nodeHeight: number;
  vGap: number;
  hGap: number;
  minWidth: number;
  padding: number;
  measureWidth: (label: string) => number;
}

/** A positioned node produced by {@link computeLayeredLayout}. */
export interface PositionedNode<T> {
  node: T;
  x: number;
  y: number;
  width: number;
}

/**
 * Generic left-to-right layered layout shared by the GeoEvent and Velocity
 * flow renderers. Identical topological-ordering + barycenter crossing
 * reduction; only the node geometry (size, gaps, width measurement) is
 * parameterised so the two diagram styles stay geometrically consistent.
 */
export function computeLayeredLayout<T extends MinimalLayoutNode>(
  nodes: T[],
  edges: Array<{ from: string; to: string }>,
  opts: LayeredLayoutOptions,
): Array<PositionedNode<T>> {
  if (nodes.length === 0) return [];

  const { nodeHeight, vGap, hGap, minWidth, padding, measureWidth } = opts;

  const adj = new Map<string, string[]>();
  const inDegree = new Map<string, number>();
  for (const node of nodes) {
    adj.set(node.id, []);
    inDegree.set(node.id, 0);
  }
  for (const edge of edges) {
    adj.get(edge.from)?.push(edge.to);
    inDegree.set(edge.to, (inDegree.get(edge.to) ?? 0) + 1);
  }

  const layer = new Map<string, number>();
  const queue: string[] = [];

  for (const node of nodes) {
    if ((inDegree.get(node.id) ?? 0) === 0) {
      queue.push(node.id);
      layer.set(node.id, 0);
    }
  }

  // If no roots found (cycle), start from first node
  if (queue.length === 0 && nodes.length > 0) {
    const firstNode = nodes[0]!;
    queue.push(firstNode.id);
    layer.set(firstNode.id, 0);
  }

  let maxLayer = 0;
  while (queue.length > 0) {
    const current = queue.shift()!;
    const currentLayer = layer.get(current) ?? 0;
    for (const next of adj.get(current) ?? []) {
      const existingLayer = layer.get(next);
      const newLayer = currentLayer + 1;
      if (existingLayer === undefined || newLayer > existingLayer) {
        layer.set(next, newLayer);
        maxLayer = Math.max(maxLayer, newLayer);
      }
      const allPredAssigned = edges
        .filter((e) => e.to === next)
        .every((e) => layer.has(e.from));
      if (allPredAssigned && !queue.includes(next)) {
        queue.push(next);
      }
    }
  }

  for (const node of nodes) {
    if (!layer.has(node.id)) {
      layer.set(node.id, 0);
    }
  }

  // Group nodes by layer
  const layers: T[][] = [];
  for (let i = 0; i <= maxLayer; i++) layers.push([]);
  for (const node of nodes) {
    const l = layer.get(node.id) ?? 0;
    const layerArr = layers[l];
    if (layerArr) layerArr.push(node);
  }

  // Crossing reduction: order nodes within each layer using barycenter heuristic.
  const reverseAdj = new Map<string, string[]>();
  for (const node of nodes) reverseAdj.set(node.id, []);
  for (const edge of edges) {
    reverseAdj.get(edge.to)?.push(edge.from);
  }

  for (let pass = 0; pass < 4; pass++) {
    if (pass % 2 === 0) {
      for (let i = 1; i < layers.length; i++) {
        const prevLayer = layers[i - 1] ?? [];
        const prevPositions = new Map<string, number>();
        prevLayer.forEach((n, idx) => prevPositions.set(n.id, idx));

        const currentLayer = layers[i] ?? [];
        currentLayer.sort((a, b) => {
          const aPreds = (reverseAdj.get(a.id) ?? []).filter((id) => prevPositions.has(id));
          const bPreds = (reverseAdj.get(b.id) ?? []).filter((id) => prevPositions.has(id));
          const aBarycenter =
            aPreds.length > 0
              ? aPreds.reduce((sum, id) => sum + (prevPositions.get(id) ?? 0), 0) / aPreds.length
              : 0;
          const bBarycenter =
            bPreds.length > 0
              ? bPreds.reduce((sum, id) => sum + (prevPositions.get(id) ?? 0), 0) / bPreds.length
              : 0;
          return aBarycenter - bBarycenter;
        });
      }
    } else {
      for (let i = layers.length - 2; i >= 0; i--) {
        const nextLayer = layers[i + 1] ?? [];
        const nextPositions = new Map<string, number>();
        nextLayer.forEach((n, idx) => nextPositions.set(n.id, idx));

        const currentLayer = layers[i] ?? [];
        currentLayer.sort((a, b) => {
          const aSuccs = (adj.get(a.id) ?? []).filter((id) => nextPositions.has(id));
          const bSuccs = (adj.get(b.id) ?? []).filter((id) => nextPositions.has(id));
          const aBarycenter =
            aSuccs.length > 0
              ? aSuccs.reduce((sum, id) => sum + (nextPositions.get(id) ?? 0), 0) / aSuccs.length
              : 0;
          const bBarycenter =
            bSuccs.length > 0
              ? bSuccs.reduce((sum, id) => sum + (nextPositions.get(id) ?? 0), 0) / bSuccs.length
              : 0;
          return aBarycenter - bBarycenter;
        });
      }
    }
  }

  // Compute per-layer max width for column alignment
  const layerMaxWidths: number[] = [];
  for (const nodesInLayer of layers) {
    let maxW = minWidth;
    for (const graphNode of nodesInLayer) {
      maxW = Math.max(maxW, measureWidth(graphNode.label));
    }
    layerMaxWidths.push(maxW);
  }

  // Compute positions — left-to-right layout with variable widths
  const layoutNodes: Array<PositionedNode<T>> = [];
  const startX = padding;
  const startY = padding;

  let cumulativeX = startX;
  for (let layerIdx = 0; layerIdx < layers.length; layerIdx++) {
    const nodesInLayer = layers[layerIdx] ?? [];
    const layerWidth = layerMaxWidths[layerIdx] ?? minWidth;
    for (let nodeIdx = 0; nodeIdx < nodesInLayer.length; nodeIdx++) {
      const graphNode = nodesInLayer[nodeIdx];
      if (graphNode) {
        const nodeWidth = measureWidth(graphNode.label);
        layoutNodes.push({
          node: graphNode,
          x: cumulativeX + (layerWidth - nodeWidth) / 2,
          y: startY + nodeIdx * (nodeHeight + vGap),
          width: nodeWidth,
        });
      }
    }
    cumulativeX += layerWidth + hGap;
  }

  return layoutNodes;
}


/**
 * Computes the total dimensions needed for the diagram content
 * (excluding any legend or chrome).
 */
export function computeDimensions(layout: LayoutNode[]): { width: number; height: number } {
  if (layout.length === 0) return { width: 200, height: 50 };

  let maxX = 0;
  let maxY = 0;
  for (const ln of layout) {
    maxX = Math.max(maxX, ln.x + ln.width);
    maxY = Math.max(maxY, ln.y + NODE_HEIGHT);
  }
  // Ensure width is at least wide enough for the legend (4 items × 70px + padding)
  const LEGEND_MIN_WIDTH = 310;
  return { width: Math.max(maxX + LAYOUT_PADDING, LEGEND_MIN_WIDTH), height: maxY + LAYOUT_PADDING };
}
